import React from 'react';
import { Box, Button, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { GuideLayout, GuideTitle, OPERATORS_FB_GROUP, Section, Steps } from './GuideLayout';

const WEEKS = [
  ['Week 1. Make the page true.', 'In WDF Setup, set the wash and fold price, the ZIP codes you will actually drive, the hours, and the phone you answer. Hide the street if you work from home. Your booking page and city pages use those facts. Ads pointed at an empty page waste money.'],
  ['Week 2. Take the free customers.', 'Claim a Google Business Profile for the business name and point it at your booking page. Post the same link on Nextdoor and in two neighborhood groups you already belong to. Tell five people you know. Ask the first three finished orders for a Google review.'],
  ['Week 3. One Google Search campaign.', 'Search ads only, for the cities in your service area, about $15 a day. The ad link is your city page or booking page, not this product site. The plan is on the Google Ads page.'],
  ['Week 4. One Facebook campaign, then keep the winner.', 'Same neighborhoods, same price, same booking link. After seven days, turn off the one that did not book a pickup. Keep spending on the one that did. Add a second offer, such as Airbnb linens, only after the first offer has a repeat customer.'],
];

export default function GetCustomersPage() {
  return (
    <GuideLayout
      title="Get customers for a wash and fold route"
      description="A plan for gig workers who run wash and fold pickup. Free SEO that comes with the software, then Google Ads and Facebook ads, and the offers that book repeat laundry."
      path="/slb/get-customers"
    >
      <GuideTitle
        kicker="For gig workers"
        heading="Get customers for the route."
        lede="The software takes the order, the weight, the text, and the card. It does not knock on doors. These pages are the work of filling a wash and fold route: free pages Google can find, then paid ads, then the offers that turn one pickup into a weekly bag."
      />

      <Section title="Wash & Fold Operators">
        <Text color="gray.700" mb={4}>
          This is a community. We want every wash and fold operator to succeed — at home or at a laundromat. Share your successes. Learn from each other. Ask when you are stuck. Request to join, then wait for approval.
        </Text>
        <Button as="a" href={OPERATORS_FB_GROUP} target="_blank" rel="noopener noreferrer" colorScheme="blue" borderRadius="full">
          Join the operators group
        </Button>
      </Section>

      <Section title="Do this before you pay for a click">
        <Text color="gray.700" mb={4}>
          A gig route dies on small orders and long drives. Spend the first week on the booking page and on people who already live where you drive. Paid ads come after a stranger can open your page, see a price, and book.
        </Text>
        <Steps items={WEEKS} />
      </Section>

      <Section title="The guides">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {[
            ['/slb/get-customers/seo', 'Free SEO', 'City pages, service pages, FAQs, and a listing Google can read. This is included. You fill in the price and the ZIPs.'],
            ['/slb/get-customers/google-ads', 'Google Ads', 'One Search campaign for people already typing “wash and fold near me.” How to build it, what to refuse, and what to spend.'],
            ['/slb/get-customers/facebook-ads', 'Facebook and Instagram', 'One local campaign with a photo of folded clothes, the neighborhoods, and the price. How to create it without boosting a post.'],
            ['/slb/get-customers/campaigns', 'Campaign plans', 'Neighborhood route, weekly households, Airbnb linens, salon and gym towels, comforters, and campus move-in. Who each one is for.'],
          ].map(([href, title, body]) => (
            <Box key={href} borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5}>
              <Heading as="h3" fontSize="lg" mb={2}>{title}</Heading>
              <Text fontSize="sm" color="gray.700" mb={4}>{body}</Text>
              <Button as={RouterLink} to={href} size="sm" colorScheme="orange" borderRadius="full">Open</Button>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      <Section title="What “a customer” means">
        <VStack align="stretch" spacing={3}>
          <Text color="gray.700">The first order pays for the drive. The second and third orders are the business. A weekly bag at your price, inside the miles you set, is the customer worth advertising for.</Text>
          <Text color="gray.700">Put the minimum on the page and in the ad. A $15 bag across town is a loss even when the click was cheap. Commercial bags (a rental’s sheets, a salon’s towels) are fewer customers and a fuller trunk. Those plans are on the campaign page. Commercial accounts in the software put several locations on one invoice.</Text>
          <Text color="gray.700">When someone finishes an order, the repeat is a weekly pickup on your booking page, and a review on Google. Referrals pay you back faster than a second ad.</Text>
        </VStack>
      </Section>
    </GuideLayout>
  );
}
